import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { PublicLayout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Mail, CheckCircle2 } from "lucide-react";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (e) {
      // Endpoint always returns 200; only network errors reach here
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };
  return (
    <PublicLayout>
      <div className="max-w-md mx-auto px-6 py-16">
        <div className="overline text-center">Account recovery</div>
        <h1 className="font-serif text-4xl text-center text-[#0B192C] mt-2">Forgot password?</h1>
        <Card className="mt-8"><CardContent className="pt-6">
          {sent ? (
            <div className="text-center py-6" data-testid="forgot-sent">
              <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-600" />
              <h2 className="font-serif text-xl mt-4 text-[#0B192C]">Check your inbox</h2>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                If <span className="font-mono">{email}</span> is registered, we've sent a password reset link. It expires in 1 hour.
              </p>
              <Link to="/login"><Button variant="outline" className="rounded-full mt-6" data-testid="forgot-back-login">Back to sign in</Button></Link>
            </div>
          ) : (
            <>
              <p className="text-sm text-slate-600 mb-5">Enter the email on your OwnNest account and we'll send a reset link.</p>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="forgot-email" />
                </div>
                <Button type="submit" className="w-full btn-gold h-11 rounded-full" disabled={loading} data-testid="forgot-submit">
                  <Mail className="h-4 w-4 mr-2" />{loading ? "Sending…" : "Send reset link"}
                </Button>
              </form>
              <div className="text-sm mt-5 text-center">
                <Link to="/login" className="text-slate-600 gold-underline" data-testid="forgot-back">Back to sign in</Link>
              </div>
            </>
          )}
        </CardContent></Card>
      </div>
    </PublicLayout>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const navigate = useNavigate();
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 6) { setErr("Password must be at least 6 characters"); return; }
    if (password !== confirm) { setErr("Passwords do not match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password reset. Please sign in.");
      setTimeout(() => navigate("/login"), 1500);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };
  return (
    <PublicLayout>
      <div className="max-w-md mx-auto px-6 py-16">
        <div className="overline text-center">Set new password</div>
        <h1 className="font-serif text-4xl text-center text-[#0B192C] mt-2">Reset password</h1>
        <Card className="mt-8"><CardContent className="pt-6">
          {!token ? (
            <div className="text-center py-6">
              <p className="text-sm text-slate-600">This reset link is invalid or has expired.</p>
              <Link to="/forgot-password"><Button variant="outline" className="rounded-full mt-4">Request a new link</Button></Link>
            </div>
          ) : done ? (
            <div className="text-center py-6" data-testid="reset-done">
              <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-600" />
              <h2 className="font-serif text-xl mt-4 text-[#0B192C]">Password updated</h2>
              <p className="mt-2 text-sm text-slate-600">Redirecting to sign in…</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" noValidate>
              <div><Label htmlFor="pw">New password</Label><Input id="pw" type="password" value={password} onChange={(e) => { setPassword(e.target.value); setErr(""); }} required data-testid="reset-password" /></div>
              <div><Label htmlFor="cf">Confirm password</Label><Input id="cf" type="password" value={confirm} onChange={(e) => { setConfirm(e.target.value); setErr(""); }} required data-testid="reset-confirm" /></div>
              {err && <div className="text-sm text-red-600" data-testid="reset-error">{err}</div>}
              <Button type="submit" className="w-full btn-gold h-11 rounded-full" disabled={loading} data-testid="reset-submit">
                {loading ? "Updating…" : "Set new password"}
              </Button>
              <div className="text-sm text-center">
                <Link to="/login" className="text-slate-600 gold-underline">Back to sign in</Link>
              </div>
            </form>
          )}
        </CardContent></Card>
      </div>
    </PublicLayout>
  );
}
